import { useEffect } from 'react';
import { getCookie, setCookie, readAllCookies } from '@/utils/cookies';
import { createClickRefV3, isValidClickref, resolveClickrefWrites } from '@/utils/clickref';

const COOKIE_EXPIRY_DAYS = 30;
const FB_POLLING_INTERVAL = 500;
const FB_POLLING_MAX_ATTEMPTS = 20; // 10 seconds max

/**
 * Get page ID from current URL path
 */
function getPageId(): string {
  if (typeof window === 'undefined') return 'unknown';
  const path = window.location.pathname;
  // Extract a clean identifier from path
  const cleanPath = path.replace(/^\/|\/$/g, '').replace(/\//g, '_') || 'home';
  return cleanPath;
}

/**
 * Component that initializes clickref tracking on page load
 */
export function ClickrefInitializer() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const cookies = readAllCookies();
    const params = new URLSearchParams(window.location.search);
    const pageId = getPageId();

    // Check existing clickref cookie
    const existingClickref = getCookie('clickref');
    if (isValidClickref(existingClickref)) {
      // Valid clickref already exists, no need to update
      return;
    }

    const writes = resolveClickrefWrites(pageId, params, cookies);

    for (const [name, value] of Object.entries(writes.cookies)) {
      setCookie(name, value, COOKIE_EXPIRY_DAYS);
    }

    if (writes.facebookTracking) {
      // For Facebook, we need to wait for the _fbc cookie
      return setupFacebookTracking(pageId, params);
    }
  }, []);

  return null;
}

/**
 * Setup Facebook pixel tracking
 * Waits for fb_loaded event and _fbc cookie
 */
function setupFacebookTracking(pageId: string, params: URLSearchParams): () => void {
  let pollTimeoutId: ReturnType<typeof setTimeout> | null = null;

  const handleFbLoaded = () => {
    let attempts = 0;

    const pollForFbc = () => {
      const fbcCookie = getCookie('_fbc');

      if (fbcCookie) {
        // Build v3 clickref with Facebook data
        const clickref = createClickRefV3({
          pid: pageId,
          fbclid: fbcCookie,
        });
        setCookie('clickref', clickref, COOKIE_EXPIRY_DAYS);
        return;
      }

      attempts++;
      if (attempts < FB_POLLING_MAX_ATTEMPTS) {
        pollTimeoutId = setTimeout(pollForFbc, FB_POLLING_INTERVAL);
      } else {
        // Fallback: use fbclid from URL if _fbc cookie not available
        const fbclid = params.get('fbclid');
        if (fbclid) {
          const clickref = createClickRefV3({
            pid: pageId,
            fbclid: fbclid,
          });
          setCookie('clickref', clickref, COOKIE_EXPIRY_DAYS);
        }
      }
    };

    pollForFbc();
  };

  // Listen for fb_loaded event (triggered by GTM after FB pixel loads)
  window.addEventListener('fb_loaded', handleFbLoaded, { once: true });

  // Also start polling immediately in case FB pixel is already loaded
  // or if event is not fired
  const fallbackTimeoutId = setTimeout(() => {
    const fbcCookie = getCookie('_fbc');
    if (fbcCookie && !isValidClickref(getCookie('clickref'))) {
      const clickref = createClickRefV3({
        pid: pageId,
        fbclid: fbcCookie,
      });
      setCookie('clickref', clickref, COOKIE_EXPIRY_DAYS);
    }
  }, 2000);

  return () => {
    window.removeEventListener('fb_loaded', handleFbLoaded);
    clearTimeout(fallbackTimeoutId);
    if (pollTimeoutId !== null) {
      clearTimeout(pollTimeoutId);
    }
  };
}

export default ClickrefInitializer;
